import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Star, Send, CheckCircle, Clock, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TestimonialsPage() {
  const { user } = useAuth();
  const [myTestimonial, setMyTestimonial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState('');
  const [role, setRole] = useState('');

  const fetchMyTestimonial = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/testimonials/my`, {
        withCredentials: true,
      });
      setMyTestimonial(res.data.testimonial);
    } catch (err) { logger.error("Operation failed:", err); } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchMyTestimonial();
  }, [fetchMyTestimonial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!quote.trim() || quote.trim().length < 20) {
      toast.error('Please write at least 20 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/testimonials/submit`,
        { quote: quote.trim(), role: role.trim() },
        { withCredentials: true }
      );
      toast.success('Testimonial submitted for review!');
      fetchMyTestimonial();
      setQuote('');
      setRole('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit testimonial');
    } finally {
      setSubmitting(false);
    }
  };

  const statusConfig = {
    pending: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', label: 'Pending review' },
    approved: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', label: 'Approved & live' },
    rejected: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20', label: 'Not approved' },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-white/30" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl" data-testid="testimonials-page">
      <h1 className="font-space text-2xl text-white mb-2">Your Testimonial</h1>
      <p className="text-white/45 text-sm mb-8">
        Share your experience with SendPI. Approved testimonials appear on our landing page.
      </p>

      {myTestimonial ? (
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 space-y-4" data-testid="existing-testimonial">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={`star-${i}`} className="w-4 h-4 fill-amber-400 text-amber-400" />
              ))}
            </div>
            {(() => {
              const s = statusConfig[myTestimonial.status] || statusConfig.pending;
              const Icon = s.icon;
              return (
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded-full border ${s.bg} ${s.color}`}>
                  <Icon className="w-3.5 h-3.5" /> {s.label}
                </span>
              );
            })()}
          </div>
          <p className="text-white/70 text-sm italic leading-relaxed">"{myTestimonial.quote}"</p>
          {myTestimonial.role && (
            <p className="text-white/40 text-xs">&mdash; {myTestimonial.role}</p>
          )}
          <p className="text-white/20 text-xs">
            Submitted {new Date(myTestimonial.created_at).toLocaleDateString()}
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 space-y-5" data-testid="testimonial-form">
          <div>
            <Label className="text-white/70 mb-2 block">Your testimonial</Label>
            <Textarea
              value={quote}
              onChange={(e) => setQuote(e.target.value)}
              placeholder="How has SendPI helped your communication? What was your aha moment?"
              className="bg-white/[0.04] border-white/[0.08] text-white min-h-[120px] focus:border-[var(--pi-violet)]/40"
              maxLength={500}
              data-testid="testimonial-quote-input"
            />
            <p className="text-white/20 text-xs mt-1">{quote.length}/500</p>
          </div>
          <div>
            <Label className="text-white/70 mb-2 block">Your role (optional)</Label>
            <Input
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g., Product Manager, Founder, Recruiter"
              className="bg-white/[0.04] border-white/[0.08] text-white focus:border-[var(--pi-violet)]/40"
              maxLength={100}
              data-testid="testimonial-role-input"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting || quote.trim().length < 20}
            className="shimmer-btn text-white rounded-full px-6 border-0"
            data-testid="testimonial-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Submit testimonial
          </Button>
          <p className="text-white/25 text-xs">Your testimonial will be reviewed before being published.</p>
        </form>
      )}
    </div>
  );
}
